"use client";

import clsx from "clsx";
import Image from "next/image";
import { usePathname } from "next/navigation";

import type { FragmentType } from "../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../__generated__/gql";
import { PersonIcon } from "../../../../_components/dx-icons";
import { Link } from "../../../../_components/ui/link";
import { DxButton } from "../../_components/dx-button";
import { DxImagePlaceholder } from "../../_components/dx-image-placeholder";

import { buildCapacityLabels } from "./_utils/build-capacity-labels";

// sidebar 内の site card に「現在地」状態を出すため、 path から site slug を抽出する。
function extractCurrentSiteSlug(pathname: string | null): string | null {
  if (pathname === null) return null;
  const match = pathname.match(/^\/areas\/[^/]+\/sites\/([^/]+)/);
  return match ? (match[1] ?? null) : null;
}

export const AreaSidebarFragment = graphql(`
  fragment AreaSidebar_Area on Area {
    slug
    name
    content {
      shortAddress
      accessFromTokyoDescription
    }
    sites {
      id
      slug
      name
      accommodations {
        id
        architectureType {
          id
          seriesShortName
          content {
            id
            capacityDescription
          }
        }
        roomType {
          id
          features {
            slug
            displayName
            sort
          }
        }
      }
      content {
        heroImages {
          url
        }
      }
    }
  }
`);

function joinAddressAndAccess(
  shortAddress: string | null | undefined,
  accessFromTokyo: string | null | undefined,
) {
  const parts = [shortAddress, accessFromTokyo].filter(
    (s): s is string => typeof s === "string" && s !== "",
  );
  return parts.length > 0 ? parts.join(" | ") : null;
}

function uniqueSeriesShortNames(
  accommodations: ReadonlyArray<{
    architectureType: { seriesShortName: string } | null;
  }>,
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const a of accommodations) {
    const s = a.architectureType?.seriesShortName;
    if (s !== undefined && !seen.has(s)) {
      seen.add(s);
      result.push(s);
    }
  }
  return result;
}

// 「代表的な特徴」を全部出すと sidebar が縦に長くなるので、 デザイナー指定の優先順位で
// 上位 2 件のみ表示する。
const FEATURE_PRIORITY_SLUGS: readonly string[] = [
  "sauna",
  "onsen",
  "dog-friendly",
  "display",
];
const FEATURE_DISPLAY_LIMIT = 2;

function topFeatureLabels(
  accommodations: ReadonlyArray<{
    roomType?: {
      features: ReadonlyArray<{ slug: string; displayName: string }>;
    } | null;
  }>,
): { labels: string[]; hasMore: boolean } {
  const seenSlugs = new Map<string, string>(); // slug → displayName
  for (const a of accommodations) {
    for (const f of a.roomType?.features ?? []) {
      if (!seenSlugs.has(f.slug)) seenSlugs.set(f.slug, f.displayName);
    }
  }
  const matched = FEATURE_PRIORITY_SLUGS.filter((slug) =>
    seenSlugs.has(slug),
  ).map((slug) => seenSlugs.get(slug) ?? slug);
  return {
    labels: matched.slice(0, FEATURE_DISPLAY_LIMIT),
    hasMore: matched.length > FEATURE_DISPLAY_LIMIT,
  };
}

export function AreaSidebar({
  area: areaData,
}: {
  area: FragmentType<typeof AreaSidebarFragment>;
}) {
  const area = useFragment(AreaSidebarFragment, areaData);
  const shortAddressLine = joinAddressAndAccess(
    area.content.shortAddress,
    area.content.accessFromTokyoDescription,
  );
  const pathname = usePathname();
  const currentSiteSlug = extractCurrentSiteSlug(pathname);
  // エリア詳細ページ自身でも「エリア情報」ボタンを Disabled で表示する
  // (この場所に機能があることを常に示す。 DXPJ-62 デザインレビュー)。
  const isOnAreaIndex = pathname === `/areas/${area.slug}`;
  return (
    // lg+ のみ render (SP は AreaSpHeader 側がエリア名を担う)。 h1 が両側で出るが
    // hidden 排他で同時 render は起きない。 SP で sidebar を出さないのはデザイン判断。
    <aside className="bg-dx-surface-base relative hidden lg:sticky lg:top-[5rem] lg:flex lg:h-[calc(100vh-5rem)] lg:w-[400px] lg:flex-none lg:flex-col">
      <header className="flex shrink-0 items-start px-[20px] pt-[8px] pb-[8px]">
        <div className="min-w-0 flex-1 px-[12px] pt-[6px]">
          <h1 className="dx-headline-2-desktop text-dx-text-default">
            {`${area.name}エリア`}
          </h1>
          {shortAddressLine !== null && (
            <div className="flex pb-[8px]">
              <p className="dx-body-3 text-dx-text-sub w-[180px]">
                {shortAddressLine}
              </p>
            </div>
          )}
        </div>
        <div className="flex min-h-[48px] items-center pr-[12px]">
          {isOnAreaIndex ? (
            <DxButton
              variant="ghost"
              disabled
              ariaLabel="エリア情報"
              // 現在地を示す disabled 表示で押せない
              data-analytics-ignore
            >
              エリア情報
            </DxButton>
          ) : (
            <DxButton
              variant="ghost"
              href={`/areas/${area.slug}`}
              data-analytics-id="area_sidebar_clicked_area_info"
            >
              エリア情報
            </DxButton>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto pb-[34px]">
        {area.sites.map((site, index) => {
          const seriesLabel =
            uniqueSeriesShortNames(site.accommodations).join(" / ") || null;
          // 複数建築タイプの定員を "2名, 4名, 6名" と列挙 (DXPJ-62)。
          const capacityLabel =
            buildCapacityLabels(site.accommodations).join(", ") || null;
          const { labels: featureLabels, hasMore } = topFeatureLabels(
            site.accommodations,
          );
          const cover = site.content.heroImages[0]?.url;
          const isSelected = currentSiteSlug === site.slug;
          return (
            <div key={site.id}>
              {index > 0 && (
                <div className="bg-dx-border-default mx-[32px] h-px" />
              )}
              <Link
                href={`/areas/${area.slug}/sites/${site.slug}`}
                aria-current={isSelected ? "page" : undefined}
                data-analytics-id="area_sidebar_clicked_site"
                className={clsx(
                  "flex items-start gap-[16px] px-[32px] py-[12px] transition-colors duration-150",
                  // 現在地は selected 固定 (hover で色変化させると誤クリック誘導になる)。
                  isSelected
                    ? "bg-dx-bg-selected"
                    : "bg-dx-surface-base hover:bg-dx-bg-base-hover",
                )}
              >
                {cover !== undefined ? (
                  <div className="bg-dx-surface-subtle relative size-[144px] flex-none overflow-hidden">
                    <Image
                      src={cover}
                      alt=""
                      fill
                      className="object-cover"
                      sizes="144px"
                    />
                  </div>
                ) : (
                  <DxImagePlaceholder
                    className="size-[144px] flex-none"
                    label="Site"
                  />
                )}
                <div className="flex min-w-0 flex-1 flex-col self-stretch">
                  <div className="flex flex-col gap-[2px]">
                    <p className="dx-body-1-bold text-dx-text-default">
                      {site.name}
                    </p>
                    {seriesLabel !== null && (
                      <p className="dx-body-3 text-dx-text-sub">
                        {seriesLabel}
                      </p>
                    )}
                  </div>
                  {capacityLabel !== null && (
                    <p className="dx-body-3 text-dx-text-sub flex items-start gap-[6px] pt-[2px]">
                      <span className="dx-icon-baseline">
                        <PersonIcon size={16} />
                      </span>
                      <span>{capacityLabel}</span>
                    </p>
                  )}
                  {featureLabels.length > 0 && (
                    // 「ほか」は特徴でなく "他にもある" マーカーなので / 区切りに含めない。
                    <p className="dx-body-2 text-dx-text-default pt-[6px]">
                      {featureLabels.join(" / ")}
                      {hasMore && " ほか"}
                    </p>
                  )}
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
